/** Enumerations shared by the API, the web app and the admin app (spec §4, §5). */

export const USER_STATUS = ['active', 'restricted', 'suspended', 'closed'] as const;
export type UserStatus = (typeof USER_STATUS)[number];

export const BOT_STATUS = ['draft', 'active', 'suspended', 'archived'] as const;
export type BotStatus = (typeof BOT_STATUS)[number];

export const RUNTIME_STATE = ['stopped', 'running', 'paused'] as const;
export type RuntimeState = (typeof RUNTIME_STATE)[number];

export const BOT_VISIBILITY = ['private', 'unlisted', 'listed'] as const;
export type BotVisibility = (typeof BOT_VISIBILITY)[number];

/**
 * Supported platforms. §0.2 originally excluded X; migration 004 reverses that
 * and X is now the first target, driven by a headless browser rather than an
 * API. The set stays closed — adding a member still means a migration, an
 * adapter and a recorded sign-off against OPEN-QUESTIONS Q1.
 */
export const PLATFORM = ['x', 'bluesky', 'mastodon', 'reddit'] as const;
export type Platform = (typeof PLATFORM)[number];

/**
 * How an account is driven. Owners bring their own credentials and choose this
 * per account: `api` uses the platform's official API with the owner's own
 * developer credentials, `browser` drives a logged-in session headlessly.
 *
 * This is a per-account choice, not a platform property. One owner may hold X
 * API access and another may not, and a single bot may hold accounts driven
 * both ways.
 */
export const TRANSPORT = ['api', 'browser'] as const;
export type Transport = (typeof TRANSPORT)[number];

/**
 * Which transports each platform has an adapter for. A platform with only `api`
 * here cannot be browser-driven no matter what an owner selects.
 */
export const PLATFORM_TRANSPORTS: Record<Platform, readonly Transport[]> = {
  x: ['api', 'browser'],
  bluesky: ['api'],
  mastodon: ['api'],
  reddit: ['api'],
};

export function supportsTransport(platform: Platform, transport: Transport): boolean {
  return PLATFORM_TRANSPORTS[platform].includes(transport);
}

/**
 * Which credential shape each (platform, transport) pair actually accepts.
 *
 * This exists because the API took an X account with `app_password` — a Bluesky
 * concept X has never had — stored the owner's *account password*, reported the
 * account healthy, and only failed when something tried to read a timeline. The
 * migration that added `session` and `api_key` says plainly that storing
 * passwords "makes this database a single object whose compromise hands over
 * every account on the platform". Saying it was not enough; nothing checked.
 *
 * `app_password` appears only for Bluesky, which genuinely issues revocable
 * per-application passwords. There is no entry anywhere for an account password,
 * because there is no combination for which one is the right answer.
 */
export const ACCEPTED_AUTH_TYPES: Record<Platform, Partial<Record<Transport, readonly AuthType[]>>> = {
  // X has no username/password API auth and has not since basic auth was
  // removed. The API path needs a user-context OAuth 2 token; an app-only
  // bearer token authenticates the *app*, not the account, and cannot post.
  x: { api: ['oauth2', 'api_key'], browser: ['session'] },
  bluesky: { api: ['app_password'] },
  mastodon: { api: ['oauth2'] },
  reddit: { api: ['oauth2'] },
};

export function acceptedAuthTypes(
  platform: Platform,
  transport: Transport,
): readonly AuthType[] {
  return ACCEPTED_AUTH_TYPES[platform][transport] ?? [];
}

/**
 * A human explanation of what to paste, per pair. The connect form shows this
 * next to the field, because "secret" means something different on every
 * platform and an unlabelled box invites the most dangerous possible answer.
 */
export const CREDENTIAL_GUIDANCE: Record<string, string> = {
  'x:api':
    'An OAuth 2.0 user-context access token with tweet.read, tweet.write and users.read. ' +
    'Not the developer portal Bearer Token — that one authenticates the app, not the account, and cannot post.',
  'x:browser':
    'A session blob captured from a login you performed yourself. Never a password, never a TOTP seed.',
  'bluesky:api':
    'A Bluesky app password from Settings → App Passwords. Never your account password.',
  'mastodon:api': 'An OAuth 2 access token for your instance, with write:statuses.',
  'reddit:api': 'An OAuth 2 refresh token for a script or web app you own.',
};

export function credentialGuidance(platform: Platform, transport: Transport): string {
  return CREDENTIAL_GUIDANCE[`${platform}:${transport}`] ?? 'A token issued by the platform.';
}

/**
 * `session` is captured cookies plus local storage from a login the owner
 * performed themselves, and is the only credential shape the browser transport
 * accepts. There is deliberately no `password` and no `totp_seed` member:
 * storing those is the difference between a security incident and handing over
 * permanent control of every connected account.
 *
 * `api_key` is the owner's own developer credential for the api transport. The
 * platform holds no shared app key — that would make every bot's traffic one
 * revocable identity.
 */
export const AUTH_TYPE = ['oauth2', 'app_password', 'session', 'api_key'] as const;
export type AuthType = (typeof AUTH_TYPE)[number];

/** Mirrors the `social_accounts_transport_auth_match` CHECK constraint. */
export function authTypeMatchesTransport(auth: AuthType, transport: Transport): boolean {
  return transport === 'browser' ? auth === 'session' : auth !== 'session';
}

export const SOCIAL_ACCOUNT_STATUS = [
  'healthy',
  /** Routine and expected under session auth, not an error path. */
  'needs_reauth',
  'rate_limited',
  /** The platform is interrupting with a challenge the owner must clear. */
  'challenged',
  'locked',
  'suspended',
  'banned',
  'revoked',
] as const;
export type SocialAccountStatus = (typeof SOCIAL_ACCOUNT_STATUS)[number];

/**
 * Statuses that require the owner to act and that block a bot being rented.
 * Under browser automation these are the normal operating condition, not the
 * exception, so the product is built around them rather than treating them as
 * incidents (§10.4).
 */
export const OWNER_ACTION_REQUIRED_STATUSES: readonly SocialAccountStatus[] = [
  'needs_reauth',
  'challenged',
  'locked',
  'suspended',
  'banned',
];

/**
 * Whether a bot answers its own direct messages, and who signs off.
 *
 * `off` is the manual inbox — a person reads and types. It is a real state, not
 * the absence of a setting, which is why this is not a boolean.
 *
 * Never implied by a capability. Granting `dm` says a renter *may* send; this
 * says the bot sends *by itself*. An owner who grants DM has not thereby asked
 * for automation.
 */
export const DM_REPLY_MODE = ['off', 'approve', 'auto'] as const;
export type DmReplyMode = (typeof DM_REPLY_MODE)[number];

export const DM_REPLY_MODE_COPY: Record<DmReplyMode, { label: string; detail: string }> = {
  off: {
    label: 'Manual',
    detail: 'Nothing is drafted. Read the thread and write the reply yourself.',
  },
  approve: {
    label: 'Draft for approval',
    detail: 'The bot writes a reply and holds it until someone approves it. Nothing sends on its own.',
  },
  auto: {
    label: 'Reply automatically',
    detail:
      'The bot writes and sends replies without waiting. It still refuses cold threads and stops at the daily caps.',
  },
};

export const LISTING_STATUS = ['active', 'paused', 'archived'] as const;
export type ListingStatus = (typeof LISTING_STATUS)[number];

export const PERIOD_UNIT = ['day', 'week', 'month'] as const;
export type PeriodUnit = (typeof PERIOD_UNIT)[number];

/** §14 Q4: hourly is excluded deliberately. */
export const PERIOD_UNIT_HOURS: Record<PeriodUnit, number> = {
  day: 24,
  week: 24 * 7,
  month: 24 * 30,
};

export const OVERAGE_BEARER = ['renter', 'owner'] as const;
export type OverageBearer = (typeof OVERAGE_BEARER)[number];

export const RENTAL_STATUS = [
  'requested',
  'declined',
  'expired_request',
  'scheduled',
  'active',
  'completed',
  'cancelled_by_renter',
  'cancelled_by_owner',
  'terminated_moderation',
  'terminated_account_failure',
] as const;
export type RentalStatus = (typeof RENTAL_STATUS)[number];

export const TERMINAL_RENTAL_STATUSES: readonly RentalStatus[] = [
  'declined',
  'expired_request',
  'completed',
  'cancelled_by_renter',
  'cancelled_by_owner',
  'terminated_moderation',
  'terminated_account_failure',
];

/**
 * Statuses that occupy a date range on a bot's calendar (FR-3.5).
 * Mirrors the WHERE predicate of the exclusion constraint; the DB is
 * authoritative, this list exists so the UI can grey out the same ranges.
 */
export const RANGE_OCCUPYING_RENTAL_STATUSES: readonly RentalStatus[] = [
  'scheduled',
  'active',
  'completed',
];

export const RENTAL_TERMINATED_REASON = [
  'account_failure',
  'owner_cancel',
  'renter_cancel',
  'moderation',
] as const;
export type RentalTerminatedReason = (typeof RENTAL_TERMINATED_REASON)[number];

export const SUBSCRIPTION_STATUS = ['active', 'past_due', 'cancelled'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[number];

/** Video is deferred to Phase 3 and has no enum member yet (§4.2 GenerationJob). */
export const GENERATION_KIND = ['text', 'image'] as const;
export type GenerationKind = (typeof GENERATION_KIND)[number];

export const GENERATION_MODE = ['preload', 'on_demand'] as const;
export type GenerationMode = (typeof GENERATION_MODE)[number];

export const GENERATION_STATUS = [
  'queued',
  'moderating',
  'running',
  'succeeded',
  'failed',
  'blocked',
] as const;
export type GenerationStatus = (typeof GENERATION_STATUS)[number];

export const ACTION_STATUS = [
  'draft',
  'scheduled',
  'approving',
  'sending',
  'sent',
  'failed',
  'blocked',
  'cancelled',
] as const;
export type ActionStatus = (typeof ACTION_STATUS)[number];

export const ACTOR_ROLE = ['owner', 'renter'] as const;
export type ActorRole = (typeof ACTOR_ROLE)[number];

/** §9.2 chart of accounts. There is no escrow and no payout account. */
export const LEDGER_ACCOUNT = [
  'external:stripe',
  'platform:revenue:creation',
  'platform:revenue:listing',
  'platform:revenue:generation',
  'platform:deferred:credits',
  'platform:refunds',
] as const;
export type LedgerAccount = (typeof LEDGER_ACCOUNT)[number];

export const GENERATION_FEE_MODE = ['flat', 'cost_plus'] as const;
export type GenerationFeeMode = (typeof GENERATION_FEE_MODE)[number];

export const CLOCK_DISPOSITION = ['owner_caused', 'renter_caused'] as const;
export type ClockDisposition = (typeof CLOCK_DISPOSITION)[number];

/**
 * A matched post's lifecycle for a listening rule (auto-reply).
 *
 * `pending` is scored on a later poll tick rather than the one that found it —
 * see `services/listening.ts` for the per-tick cap that makes that necessary.
 * `needs_review` covers both "below the confidence threshold" and "over the
 * rule's daily cap": both leave the owner a draft to send or dismiss by hand
 * rather than losing the candidate.
 */
export const LISTENING_MATCH_STATUS = [
  'pending',
  'needs_review',
  'auto_sent',
  'sent',
  'dismissed',
  'score_failed',
] as const;
export type ListeningMatchStatus = (typeof LISTENING_MATCH_STATUS)[number];
